// Small UI kit (Tailwind). Visual language of the client's sample portal:
// cream background, charcoal header and primary buttons, gold accents.
import { Check, Copy, Download, Loader2, type LucideIcon, Upload, X } from "lucide-react";
import { type ButtonHTMLAttributes, type ReactNode, useEffect, useId, useRef, useState } from "react";

const cx = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(" ");

export const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-xs placeholder:text-gray-400 focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-300 disabled:bg-gray-50 disabled:text-gray-500";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  icon: Icon,
  className,
  children,
  disabled,
  type = "button",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm" | "md";
  loading?: boolean;
  icon?: LucideIcon;
}) {
  const variants: Record<ButtonVariant, string> = {
    primary: "bg-charcoal text-white hover:bg-black",
    secondary: "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50",
    ghost: "text-gray-600 hover:bg-gray-100",
    danger: "text-rose-700 hover:bg-rose-50",
  };
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cx(
        "inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "px-2.5 py-1.5 text-xs" : "px-3.5 py-2 text-sm",
        variants[variant],
        className,
      )}
      {...props}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : (
        Icon && <Icon className="h-4 w-4" aria-hidden />
      )}
      {children}
    </button>
  );
}

/**
 * A file served by the API as an attachment (reports, documents), styled as a
 * secondary button. A plain link: the session cookie goes along and the
 * browser saves the file itself.
 */
export function DownloadLink({
  href,
  children,
  size = "md",
  icon: Icon = Download,
}: {
  href: string;
  children: ReactNode;
  size?: "sm" | "md";
  icon?: LucideIcon;
}) {
  return (
    <a
      href={href}
      download
      className={cx(
        "inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white font-medium text-gray-800 transition hover:bg-gray-50",
        size === "sm" ? "px-2.5 py-1.5 text-xs" : "px-3.5 py-2 text-sm",
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
      {children}
    </a>
  );
}

/** A button that opens the file picker and hands over the chosen file. */
export function FileButton({
  onFile,
  accept,
  label,
  children,
  disabled = false,
  variant = "secondary",
  size = "sm",
}: {
  onFile: (file: File) => void;
  accept: string;
  /** Accessible name of the file input, e.g. "Archivo: INE". */
  label: string;
  children: ReactNode;
  disabled?: boolean;
  variant?: ButtonVariant;
  size?: "sm" | "md";
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        aria-label={label}
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset, so choosing the same file again still triggers a change.
          e.target.value = "";
          if (file) onFile(file);
        }}
      />
      <Button variant={variant} size={size} icon={Upload} disabled={disabled} onClick={() => input.current?.click()}>
        {children}
      </Button>
    </>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("rounded-xl border border-gray-200 bg-white shadow-sm", className)}>{children}</div>;
}

/** Label + control + hint/error. `children` receives the generated id. */
export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-700">
        {label}
        {required && <span className="text-rose-600"> *</span>}
      </label>
      {children(id)}
      {error ? (
        <p className="mt-1 text-xs text-rose-600">{error}</p>
      ) : (
        hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>
      )}
    </div>
  );
}

export type Tone = "neutral" | "green" | "amber" | "red" | "gold" | "blue";

export function Pill({
  tone = "neutral",
  children,
  icon: Icon,
}: {
  tone?: Tone;
  children: ReactNode;
  icon?: LucideIcon;
}) {
  const tones: Record<Tone, string> = {
    neutral: "bg-gray-100 text-gray-700",
    green: "border border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border border-amber-200 bg-amber-50 text-amber-800",
    red: "border border-rose-200 bg-rose-50 text-rose-700",
    gold: "bg-gold-700 text-white",
    blue: "border border-sky-200 bg-sky-50 text-sky-700",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium",
        tones[tone],
      )}
    >
      {Icon && <Icon className="h-3 w-3" aria-hidden />}
      {children}
    </span>
  );
}

export function EmptyState({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 px-6 py-12 text-center">
      <Icon className="mb-3 h-8 w-8 text-gray-300" aria-hidden />
      <p className="text-sm font-medium text-gray-700">{title}</p>
      {children && <div className="mt-1 max-w-sm text-sm text-gray-500">{children}</div>}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-gray-500" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {label}
    </span>
  );
}

export function PageLoader({ label = "Cargando…" }: { label?: string }) {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner label={label} />
    </div>
  );
}

export function Banner({
  tone = "info",
  title,
  children,
}: {
  tone?: "info" | "warning" | "error" | "success";
  title?: string;
  children: ReactNode;
}) {
  const tones = {
    info: "border-sky-200 bg-sky-50 text-sky-900",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    error: "border-rose-200 bg-rose-50 text-rose-900",
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  };
  return (
    <div
      className={cx("rounded-lg border px-4 py-3 text-sm", tones[tone])}
      role={tone === "error" ? "alert" : "status"}
    >
      {title && <p className="font-semibold">{title}</p>}
      <div className={title ? "mt-0.5" : undefined}>{children}</div>
    </div>
  );
}

/** Modal built on the native <dialog> element (focus trap and Esc for free). */
export function Modal({
  open,
  onClose,
  title,
  children,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className={cx(
        "m-auto w-[calc(100%-2rem)] rounded-xl border border-gray-200 bg-white p-0 shadow-xl backdrop:bg-black/40",
        wide ? "max-w-3xl" : "max-w-lg",
      )}
    >
      {open && (
        <div>
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[75vh] overflow-y-auto px-5 py-4">{children}</div>
        </div>
      )}
    </dialog>
  );
}

export function CopyButton({ text, label = "Copiar" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <Button variant="secondary" size="sm" icon={copied ? Check : Copy} onClick={copy}>
      {copied ? "Copiado" : label}
    </Button>
  );
}

export function SectionTitle({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

/** Horizontally scrollable table wrapper for small screens. */
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full min-w-[640px] text-left text-sm">{children}</table>
    </div>
  );
}

export const th = "px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500";
export const td = "px-4 py-2.5 align-middle";
